
import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { Search, MapPin, Hotel, DollarSign, Users, Calendar } from 'lucide-react';
import { ThemeSwitcher } from '../UI/ThemeSwitcher';

interface HotelLayoutProps {
  children: React.ReactNode;
  className?: string;
  transparentHeader?: boolean;
}

const HotelLayout: React.FC<HotelLayoutProps> = ({ 
  children, 
  className,
  transparentHeader = false
}) => {
  const { resolvedTheme } = useTheme();

  return (
    <div className={`min-h-screen ${resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background'}`}>
      {/* Header */}
      <header 
        className={cn(
          'fixed top-0 left-0 right-0 z-50 flex items-center justify-center transition-all duration-300',
          resolvedTheme === 'dark' ? 'bg-netflix-black' : 'bg-background',
          'h-16 md:h-[72px] border-b border-border'
        )}
      >
        <div className="container mx-auto flex items-center justify-between px-4">
          <div className="flex items-center">
            <Link to="/hotels" className="text-xl font-bold text-primary flex items-center">
              <Hotel className="mr-2" size={24} />
              <span>HotelFinder</span>
            </Link>
          </div>
          
          <div className="hidden md:flex flex-1 justify-center space-x-6">
            <Link to="/hotels" className="text-foreground/80 hover:text-primary transition-colors">Home</Link>
            <Link to="/hotels/search" className="text-foreground/80 hover:text-primary transition-colors">Search</Link>
            <Link to="/hotels/deals" className="text-foreground/80 hover:text-primary transition-colors">Deals</Link>
            <Link to="/hotels/reviews" className="text-foreground/80 hover:text-primary transition-colors">Reviews</Link>
          </div>
          
          <div className="flex items-center space-x-4">
            <ThemeSwitcher />
            <Link to="/" className="text-foreground/80 hover:text-primary transition-colors">
              Back to Main Site
            </Link>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className={cn('pt-16 md:pt-[72px]', className)}>
        {children}
      </main>
      
      {/* Footer */}
      <footer className={cn(
        'py-8 mt-16 border-t',
        resolvedTheme === 'dark' ? 'bg-netflix-black border-border' : 'bg-background border-border'
      )}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">HotelFinder</h3>
              <p className="text-foreground/70">
                Find the best hotel deals across multiple booking sites.
                Compare prices and save on your next trip.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/hotels" className="text-foreground/70 hover:text-primary">Home</Link></li>
                <li><Link to="/hotels/search" className="text-foreground/70 hover:text-primary">Search Hotels</Link></li>
                <li><Link to="/hotels/deals" className="text-foreground/70 hover:text-primary">Best Deals</Link></li>
                <li><Link to="/" className="text-foreground/70 hover:text-primary">Main Website</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Contact</h3>
              <p className="text-foreground/70">
                Have questions? <br />
                Email: support@hotelfinder.com <br />
                Phone: +1 (555) 123-4567
              </p>
            </div>
          </div>
          <div className="border-t border-border mt-8 pt-6 text-center text-foreground/70">
            <p>© {new Date().getFullYear()} HotelFinder. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HotelLayout;
